import React, { useEffect, useId, useState } from 'react'
import { ContactsOutlined, FileTextOutlined, InboxOutlined, DownOutlined } from '@ant-design/icons'
import { InvoiceField, InvoiceFieldsEditor } from '../invoice-fields'
import { DocumentDraft, ReviewDocument } from './result-model'
import AdditionalFields from './additional-fields'

const groups = [
  {
    key: 'parties',
    title: 'Контрагенты',
    icon: <ContactsOutlined />,
    codes: ['supplier_tax_id', 'buyer_tax_id', 'buyer', 'supplier_address', 'buyer_address'],
  },
  {
    key: 'delivery',
    title: 'Поставка',
    icon: <InboxOutlined />,
    codes: [
      'cargo_places',
      'packaging_type',
      'shipped_at',
      'received_at',
      'consignor',
      'consignee',
      'supply_basis',
      'contract',
      'shipment_document',
    ],
  },
  {
    key: 'document',
    title: 'УПД и расчёты',
    icon: <FileTextOutlined />,
    codes: [
      'document_status',
      'correction_number',
      'correction_date',
      'payment_document',
      'edo_document_id',
      'edo_transferred_at',
      'edo_signature_status',
      'currency',
      'subtotal',
      'vat_amount',
      'total',
    ],
  },
]

export default function DocumentRequisites({
  definitions,
  source,
  draft,
  disabled,
  focus,
  onChange,
  onChangeAdditional,
}: {
  definitions: InvoiceField[]
  source: ReviewDocument['original']
  draft: DocumentDraft
  disabled: boolean
  focus?: { field: string; request: number }
  onChange: (values: NonNullable<DocumentDraft['field_values']>) => void
  onChangeAdditional: (values: NonNullable<DocumentDraft['additional_values']>) => void
}) {
  const [expanded, setExpanded] = useState(false)
  const contentId = useId()
  useEffect(() => {
    if (focus) {
      setExpanded(true)
    }
  }, [focus])
  const known = new Set(groups.flatMap(group => group.codes))
  const sections = [
    ...groups,
    {
      key: 'other',
      title: 'Другие реквизиты',
      icon: <FileTextOutlined />,
      codes: definitions.filter(field => !known.has(field.code)).map(field => field.code),
    },
  ]
  return (
    <section
      className={`supply-check__document-requisites${expanded ? ' is-open' : ''}`}
      aria-label="Реквизиты документа"
    >
      <div className="supply-check__document-requisites-heading">
        <button
          type="button"
          className="supply-check__document-requisites-toggle"
          aria-expanded={expanded}
          aria-controls={contentId}
          onClick={() => setExpanded(value => !value)}
        >
          <DownOutlined rotate={expanded ? 0 : -90} />
          <span>
            <strong>Реквизиты документа</strong>
            <small>Контрагенты, поставка и данные УПД</small>
          </span>
        </button>
      </div>
      <div id={contentId} className="supply-check__document-requisites-body" hidden={!expanded}>
        <div className="supply-check__requisite-groups">
          {sections.map(group => {
            const fields = group.codes.flatMap(code =>
              definitions.filter(field => field.code === code),
            )
            if (!fields.length) return null
            return (
              <section
                className={`supply-check__requisite-group supply-check__requisite-group--${group.key}`}
                key={group.key}
                aria-label={group.title}
              >
                <h4>
                  {group.icon}
                  {group.title}
                </h4>
                <InvoiceFieldsEditor
                  presentation="plain"
                  title={group.title}
                  definitions={fields}
                  source={source}
                  draft={draft}
                  disabled={disabled}
                  focus={fields.some(field => field.code === focus?.field) ? focus : undefined}
                  onChange={onChange}
                />
              </section>
            )
          })}
        </div>
        <AdditionalFields
          presentation="plain"
          title="Другие данные из документа"
          definitions={definitions}
          fields={source.additional_fields}
          values={draft.additional_values}
          disabled={disabled}
          onChange={onChangeAdditional}
        />
      </div>
    </section>
  )
}
